import React, { Component } from 'react';
import { ArrowLeftIcon, CalendarIcon, ClockIcon, MapPinIcon, UserIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/apiService';
import LoadingSpinner from '../common/LoadingSpinner';

class Events extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      events: [],
      currentPage: 0,
      totalRecords: 0,
      limit: 10,
      hasMore: false,
      loadingMore: false,
    };
    this.scrollContainerRef = React.createRef();
  }

  componentDidMount() {
    this.loadEvents();
  }

  componentWillUnmount() {
    // Clean up scroll listener
    if (this.scrollContainerRef.current) {
      this.scrollContainerRef.current.removeEventListener('scroll', this.handleScroll);
    }
  }

  loadEvents = async (page = 0) => {
    const { recordId, relationId, onLogout } = this.props;

    // Set loading state based on whether this is initial load or load more
    if (page === 0) {
      this.setState({ loading: true });
    } else {
      this.setState({ loadingMore: true });
    }
    
    try {
      const result = await apiService.getSpecialRelatedRecords(
        'GetRelatedEvents',
        recordId,
        relationId,
        'Calendar',
        page,
        this.state.limit
      );
      
      
      if (result.success) {
        const responseData = result.data;
        
        const events = responseData.listViewEntries || responseData.data || responseData.records || [];
        const totalCount = responseData.total_count || responseData.totalRecords || events.length;
        
        
        const newEvents = events;
        
        // Calculate if there are more events to load
        const currentEventCount = page === 0 ? newEvents.length : this.state.events.length + newEvents.length;
        const hasMore = currentEventCount < totalCount;
        
        this.setState({
          events: page === 0 ? newEvents : [...this.state.events, ...newEvents],
          currentPage: page,
          totalRecords: totalCount,
          hasMore: hasMore,
          loading: false,
          loadingMore: false,
        });
        
        // Add scroll listener after first load
        if (page === 0 && this.scrollContainerRef.current) {
          this.scrollContainerRef.current.addEventListener('scroll', this.handleScroll);
        }
      } else if (result.needsLogout && onLogout) {
        onLogout();
      } else {
        this.setState({ 
          loading: false, 
          loadingMore: false,
          events: page === 0 ? [] : this.state.events 
        });
      }
    } catch (error) {
      this.setState({ 
        loading: false, 
        loadingMore: false,
        events: page === 0 ? [] : this.state.events 
      });
    }
  };

  handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const { hasMore, loadingMore } = this.state;
    
    // Load more when scrolled to bottom (with 100px threshold)
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMore && !loadingMore) {
      this.loadMoreEvents();
    }
  };

  loadMoreEvents = () => {
    if (this.state.hasMore && !this.state.loadingMore) {
      const nextPage = this.state.currentPage + 1;
      this.loadEvents(nextPage);
    }
  };

  refreshEvents = () => {
    this.setState({
      events: [],
      currentPage: 0,
      hasMore: false,
    }, () => {
      this.loadEvents(0);
    });
  };

  formatEventDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  formatEventTime = (timeString) => {
    if (!timeString) return '';
    try {
      // Handle time format like "14:30:00" or full datetime
      if (timeString.includes(':')) {
        const timeParts = timeString.split(':');
        const hours = parseInt(timeParts[0]);
        const minutes = timeParts[1];
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${minutes} ${ampm}`;
      }
      return timeString;
    } catch (e) {
      return timeString;
    }
  };

  getEventStatus = (event) => {
    const now = new Date();
    const eventDate = new Date(event.date_start);
    
    if (eventDate < now) {
      return { label: 'Past', color: 'bg-gray-100 text-gray-800' };
    } else if (eventDate.toDateString() === now.toDateString()) {
      return { label: 'Today', color: 'bg-blue-100 text-blue-800' };
    } else {
      return { label: 'Upcoming', color: 'bg-green-100 text-green-800' };
    }
  };

  handleEventClick = (event) => {
    // Handle event click - could open detail view
    if (this.props.onEventSelect) {
      this.props.onEventSelect(event);
    }
  };

  render() {
    const { 
      loading, 
      events, 
      totalRecords, 
      hasMore,
      loadingMore 
    } = this.state;
    
    const { onCancel, onBack } = this.props;

    if (loading) {
      return (
        <LoadingSpinner label="Loading events..." variant="fullscreen" />
      );
    }

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-200 space-y-3">
          {/* Back Button */}
          <div>
            {onBack ? (
              <button
                onClick={onBack}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Back to Details
              </button>
            ) : (
              <button
                onClick={onCancel}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                Back
              </button>
            )}
          </div>
          
          {/* Title and Refresh */}
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <CalendarIcon className="w-5 h-5 mr-2 text-primary-600" />
              Events
            </h2>
            <button
              onClick={this.refreshEvents}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <ArrowPathIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* Pagination Summary */}
          <div>
            <p className="text-sm text-gray-500">
              Showing {events.length} of {totalRecords} events
            </p>
          </div>
        </div>

        {/* Events List */}
        <div 
          ref={this.scrollContainerRef}
          className="overflow-y-auto"
          style={{ maxHeight: 'calc(100vh - 200px)' }}
          onScroll={this.handleScroll}
        >
          {events.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {events.map((event, index) => {
                const status = this.getEventStatus(event);
                
                return (
                  <div
                    key={index}
                    className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => this.handleEventClick(event)}
                  >
                    {/* Event Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-medium text-gray-900 truncate mb-1">
                          {event.subject || event.label || event.name || 'Untitled Event'}
                        </h3>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                            {status.label}
                          </span>
                          {event.activitytype && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {event.activitytype}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Event Details */}
                    <div className="space-y-2">
                      {/* Start Date and Time */}
                      {event.date_start && (
                        <div className="flex items-center text-sm text-gray-600">
                          <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="font-medium mr-2">Start:</span>
                          <span>{event.date_start}</span>
                        </div>
                      )}
                      
                      {/* End Date and Time */}
                      {event.due_date && (
                        <div className="flex items-center text-sm text-gray-600">
                          <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="font-medium mr-2">End:</span>
                          <span>{event.due_date}</span>
                        </div>
                      )}
                      
                      {/* Activity Type */}
                      {event.activitytype && (
                        <div className="flex items-center text-sm text-gray-600">
                          <ClockIcon className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="font-medium mr-2">Type:</span>
                          <span>{event.activitytype}</span>
                        </div>
                      )}
                      
                      {/* Division */}
                      {event.division && (
                        <div className="flex items-center text-sm text-gray-600">
                        <CalendarIcon className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="font-medium mr-2">Division:</span>
                          <span>{event.division}</span>
                        </div>
                      )}
                      
                      {/* Location */}
                      {event.location && (
                        <div className="flex items-center text-sm text-gray-600">
                          <MapPinIcon className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                      
                      {/* Related Contact */}
                      {event.relatedcontactid && (
                        <div className="flex items-center text-sm text-gray-600">
                          <UserIcon className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="font-medium mr-2">Contact:</span>
                          <span className="truncate">{event.relatedcontactid}</span>
                        </div>
                      )}
                      
                      {/* Description */}
                      {event.description && (
                        <div className="text-sm text-gray-600 mt-2">
                          <p className="line-clamp-2">{event.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Load More Indicator */}
              {loadingMore && (
                <div className="flex justify-center py-4 px-4">
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                    <span className="text-sm text-gray-500">Loading more events...</span>
                  </div>
                </div>
              )}
              
              {/* End of Events Indicator */}
              {!hasMore && events.length > 0 && (
                <div className="text-center py-4 px-4 border-t border-gray-100">
                  <p className="text-xs text-gray-400">No more events to load</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 px-4">
              <div className="w-16 h-16 mx-auto mb-4 text-gray-300">
                <CalendarIcon className="w-full h-full" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Events Found</h3>
              <p className="text-sm text-gray-500">No events found for this record</p>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default Events;